// authAxios.js
import axios, { AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { getRecoil } from 'recoil-nexus';

import { logout } from '@/framework/user';
import { pidState } from '@/use/usePidState';

import { getAuthToken } from '@/api/auth-token';
import { apiBaseUrl } from '@/config/api-config';

const axiosClient = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true,
});

function getClientPidHeader(): string | undefined {
  try {
    const fromState = getRecoil(pidState);
    if (fromState) return fromState;
  } catch {
    // Recoil may be unavailable very early
  }
  return Cookies.get('pid') || undefined;
}

axiosClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  const url = config.url || '';
  if (url.includes('/api/games/')) {
    const pid = getClientPidHeader();
    if (pid) {
      config.headers = config.headers || {};
      config.headers['X-Client-Pid'] = pid;
    }
  }

  return config;
});

export const responseBody = (response: AxiosResponse) => response.data;

axiosClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error?.response?.status;

    /**
     * User made an illegal request, or the token has expired
     */
    const reqUrl = String(error?.config?.url || '');
    const skipAutoLogout =
      /\/api\/auth\/(guest|login|register|logout)/.test(reqUrl) ||
      /\/api\/user(\?|$)/.test(reqUrl);

    if (status === 401 && !skipAutoLogout) {
      logout();
    }

    return Promise.reject(error.response?.data ?? error);
  },
);

export default axiosClient;
